import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Star, Clock, ChevronRight, Home, Building2, Server, Package, BarChart3,
  Phone, Mail, ExternalLink, ChevronDown, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════
// FAVORITES BAR
// ══════════════════════════════════════════════════════════════
export function FavoritesBar({ token, onNavigate }) {
  const [favorites, setFavorites] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/user/favorites`, { headers }).then(r => setFavorites(r.data.favorites || [])).catch(() => {});
  }, [headers]);

  if (favorites.length === 0) return null;

  const TYPE_ICONS = { property: Building2, report: BarChart3, product: Package, vendor: Building2, platform: Server };

  return (
    <div className="shrink-0 px-4 py-2 border-b bg-card/30 overflow-x-auto scrollbar-none" data-testid="favorites-bar">
      <div className="flex items-center gap-1.5">
        <Star className="h-3 w-3 text-amber-500 shrink-0" />
        {favorites.map(f => {
          const Icon = TYPE_ICONS[f.type] || Star;
          return (
            <button key={f.key} onClick={() => onNavigate(f)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-card hover:bg-muted/60 text-xs font-medium whitespace-nowrap transition-colors shrink-0" data-testid={`favorite-${f.key}`}>
              <Icon className="h-3 w-3 text-muted-foreground" />{f.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Toggle favorite helper
export function useFavorites(token) {
  const [favorites, setFavorites] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const refresh = useCallback(() => {
    axios.get(`${BACKEND}/api/user/favorites`, { headers }).then(r => setFavorites(r.data.favorites || [])).catch(() => {});
  }, [headers]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(async (type, id, name) => {
    try {
      const r = await axios.post(`${BACKEND}/api/user/favorites/toggle`, { type, id, name }, { headers });
      toast.success(r.data.action === 'added' ? `Added to favorites` : `Removed from favorites`);
      refresh();
    } catch {}
  }, [headers, refresh]);

  const isFavorite = useCallback((type, id) => favorites.some(f => f.key === `${type}/${id}`), [favorites]);

  return { favorites, toggle, isFavorite, refresh };
}

// ══════════════════════════════════════════════════════════════
// RECENTLY VIEWED
// ══════════════════════════════════════════════════════════════
export function RecentlyViewed({ token, onNavigate }) {
  const [recent, setRecent] = useState([]);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/user/recent`, { headers }).then(r => setRecent(r.data.recent || [])).catch(() => {});
  }, [headers]);

  if (recent.length === 0) return null;

  const TYPE_ICONS = { property: Building2, report: BarChart3, product: Package, vendor: Building2, platform: Server };

  return (
    <div className="border rounded-xl p-4 bg-card/50" data-testid="recently-viewed">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Clock className="h-3 w-3" /> Recently Viewed
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {recent.slice(0, 10).map(r => {
          const Icon = TYPE_ICONS[r.type] || Clock;
          return (
            <button key={r.key} onClick={() => onNavigate(r)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border bg-card hover:bg-muted/60 text-xs transition-colors">
              <Icon className="h-3 w-3 text-muted-foreground" />{r.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Track a view
export function useRecentTracker(token) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  return useCallback(async (type, id, name) => {
    try { await axios.post(`${BACKEND}/api/user/recent`, { type, id, name }, { headers }); } catch {}
  }, [headers]);
}

// ══════════════════════════════════════════════════════════════
// BREADCRUMB NAVIGATION
// ══════════════════════════════════════════════════════════════
export function Breadcrumbs({ items, onNavigate }) {
  if (!items || items.length <= 1) return null;
  return (
    <nav className="shrink-0 px-4 py-1.5 border-b bg-muted/20 flex items-center gap-1 text-xs text-muted-foreground overflow-x-auto scrollbar-none" data-testid="breadcrumbs">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
          {i < items.length - 1 ? (
            <button onClick={() => onNavigate(item)} className="hover:text-foreground transition-colors whitespace-nowrap">{item.label}</button>
          ) : (
            <span className="text-foreground font-medium whitespace-nowrap">{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

// ══════════════════════════════════════════════════════════════
// MOBILE ORG CHART LIST VIEW
// ══════════════════════════════════════════════════════════════
export function MobileStackList({ stackData, selectedProperty, token, onSelectNode }) {
  const [expandedCats, setExpandedCats] = useState({});

  if (!stackData) return <div className="p-4 text-center text-sm text-muted-foreground">Select a property to view its tech stack.</div>;

  const nodes = stackData.nodes || [];
  const integrations = stackData.integrations || [];

  // Group by category
  const byCategory = {};
  nodes.forEach(n => {
    const cat = n.category_name || 'Other';
    if (!byCategory[cat]) byCategory[cat] = { color: n.category_color || '#888', nodes: [] };
    byCategory[cat].nodes.push(n);
  });

  // Sort: used first within each category
  Object.values(byCategory).forEach(cat => cat.nodes.sort((a, b) => (b.used ? 1 : 0) - (a.used ? 1 : 0)));

  const categories = Object.entries(byCategory).sort((a, b) => a[0].localeCompare(b[0]));

  // Integration lookup
  const integrationMap = {};
  integrations.forEach(integ => {
    const from = integ.from_platform_id || integ.source;
    const to = integ.to_platform_id || integ.target;
    if (!integrationMap[from]) integrationMap[from] = [];
    if (!integrationMap[to]) integrationMap[to] = [];
    integrationMap[from].push({ target: to, direction: integ.direction });
    integrationMap[to].push({ target: from, direction: integ.direction });
  });

  const STATUS_COLORS = {
    used: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    unused: 'bg-slate-100 text-slate-500 border-slate-200',
  };

  return (
    <ScrollArea className="flex-1 pb-mobile-nav" data-testid="mobile-stack-list">
      <div className="p-3 space-y-2">
        {/* Property header */}
        {selectedProperty && selectedProperty !== 'overall' && (
          <div className="px-1 pb-2">
            <h2 className="text-base font-bold">{stackData.property_name || selectedProperty}</h2>
            <p className="text-xs text-muted-foreground">{nodes.filter(n => n.used).length} deployed / {nodes.length} total platforms</p>
          </div>
        )}

        {categories.map(([catName, catData]) => {
          const isExpanded = expandedCats[catName] ?? (catData.nodes.some(n => n.used));
          const usedCount = catData.nodes.filter(n => n.used).length;
          return (
            <div key={catName} className="rounded-xl border overflow-hidden">
              <button
                onClick={() => setExpandedCats(p => ({ ...p, [catName]: !isExpanded }))}
                className="w-full flex items-center gap-2 p-3 bg-card hover:bg-muted/30 transition-colors"
              >
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: catData.color }} />
                <span className="text-sm font-semibold flex-1 text-left">{catName}</span>
                {usedCount > 0 && <Badge className="text-[9px] bg-emerald-100 text-emerald-700">{usedCount} active</Badge>}
                <span className="text-xs text-muted-foreground">{catData.nodes.length}</span>
                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${!isExpanded ? '-rotate-90' : ''}`} />
              </button>
              {isExpanded && (
                <div className="border-t divide-y">
                  {catData.nodes.map(node => {
                    const conns = integrationMap[node.id] || [];
                    return (
                      <button key={node.id} onClick={() => onSelectNode && onSelectNode(node)} className="w-full text-left p-3 hover:bg-muted/20 transition-colors">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-8 rounded-full shrink-0 ${node.used ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{node.name}</div>
                            {node.used && node.vendor_name && (
                              <div className="text-xs text-muted-foreground">{node.vendor_name}{node.product_name && node.product_name !== node.vendor_name ? ` — ${node.product_name}` : ''}</div>
                            )}
                          </div>
                          <Badge variant="outline" className={`text-[8px] shrink-0 ${node.used ? STATUS_COLORS.used : STATUS_COLORS.unused}`}>
                            {node.used ? 'Active' : 'Gap'}
                          </Badge>
                        </div>
                        {conns.length > 0 && (
                          <div className="mt-1.5 ml-4 flex flex-wrap gap-1">
                            {conns.slice(0, 4).map((c, i) => (
                              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                {c.direction === 'two-way' ? '↔' : '→'} {c.target}
                              </span>
                            ))}
                            {conns.length > 4 && <span className="text-[9px] text-muted-foreground">+{conns.length - 4} more</span>}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

// ══════════════════════════════════════════════════════════════
// MOBILE SUPPORT CONTACT SPEED DIAL
// ══════════════════════════════════════════════════════════════
export function SupportSpeedDial({ contacts }) {
  if (!contacts || contacts.length === 0) return null;
  return (
    <div className="space-y-1.5" data-testid="speed-dial">
      {contacts.map((c, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl border bg-card">
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium block">{c.platform_name || c.name}</span>
            {c.vendor_name && <span className="text-xs text-muted-foreground">{c.vendor_name}</span>}
          </div>
          {c.phone && (
            <a href={`tel:${c.phone}`} className="shrink-0">
              <Button size="icon" variant="outline" className="h-10 w-10 rounded-full border-emerald-200 text-emerald-600 hover:bg-emerald-50">
                <Phone className="h-4 w-4" />
              </Button>
            </a>
          )}
          {c.email && (
            <a href={`mailto:${c.email}`} className="shrink-0">
              <Button size="icon" variant="outline" className="h-10 w-10 rounded-full border-blue-200 text-blue-600 hover:bg-blue-50">
                <Mail className="h-4 w-4" />
              </Button>
            </a>
          )}
          {c.support_url && (
            <a href={c.support_url} target="_blank" rel="noreferrer" className="shrink-0">
              <Button size="icon" variant="outline" className="h-10 w-10 rounded-full border-slate-200 text-slate-600 hover:bg-slate-50">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
